import React, { Component } from "react";
import styled from "styled-components";

import { ArrowRight } from "@material-ui/icons";

import { themeColors } from "js/react/sharedStyles";
import { Gap10 } from "js/react/components/Gap";

import { MultiSelectThumbnailGrid, ThumbnailGrid } from "./ThumbnailGrid";
import { t } from "js/i18n";

const SearchPreview = styled.div`
  background: white;
  padding: 10px 20px;
  margin: 0px 0px 20px;
  overflow: hidden;
`;

const SearchPreviewTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111;
`;

const StyledHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SearchPreviewHeader = styled.div.attrs({ "data-test-id": "search-preview-header" })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  p {
    font-size: 13px;
    color: ${themeColors.ui_blue};
    text-transform: uppercase;
    font-weight: bolder;
    
    &:hover {
      cursor: pointer;
    }
  }

  .expander {
    margin-top: 2px;
    cursor: pointer;
    
    .MuiSvgIcon-root {
      font-size: 28px;
      margin: 7px -5px;
      transition: transform 300ms;
    }
    
    &.expanded {
      .MuiSvgIcon-root {
        transform: rotate(90deg);
      }
    }
  }
`;

const AddSelectionButton = styled.button`
  background: ${themeColors.ui_blue};
  color: white;
  border: none;
  border-radius: 2px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: bolder;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
`;

export const SlideSearchPreview = class SlideSearchPreview extends Component {
    constructor(props) {
        super(props);

        this.state = {
            expanded: !!props.expanded,
            selection: [],
            // The grid owns its own selection state, bumping this remounts it so the highlighted
            // thumbnails stay in sync with `selection`
            selectionResetCount: 0
        };
    }

    get allowCollapse() {
        const { allowCollapse = true } = this.props;

        return allowCollapse;
    }

    componentDidUpdate(prevProps) {
        const { expanded, items } = this.props;
        const { selection } = this.state;

        if (prevProps.expanded !== expanded) {
            this.setState({ expanded });
        }

        // A new set of results can leave the selection pointing at slides that are no longer on
        // screen, and adding those is never what the user asked for. The selection is dropped
        // wholesale rather than trimmed because the grid keeps its own copy of it — clearing is
        // the only way to keep the two in step, and it remounts the grid to do so.
        // NOTE: `items` is rebuilt on every render upstream, so this compares ids, not identity.
        if (selection.length > 0) {
            const itemIds = new Set(items.map(({ id }) => id));

            if (selection.some(({ id }) => !itemIds.has(id))) {
                this.clearSelection();
            }
        }
    }

    handleSelectionChange = selection => {
        this.setState({ selection });
    }

    clearSelection = () => {
        this.setState(({ selectionResetCount }) => ({ selection: [], selectionResetCount: selectionResetCount + 1 }));
    }

    handleAddSelection = () => {
        const { onSelected } = this.props;
        const { selection } = this.state;

        if (selection.length === 0) {
            return;
        }

        this.clearSelection();
        onSelected(selection);
    }

    handleToggleExpand = () => {
        if (!this.allowCollapse) {
            return;
        }

        // Collapsing unmounts the grid, so its own selection goes with it — this just stops the
        // button offering slides the user can no longer see
        this.setState(({ expanded }) => expanded
            ? { expanded: false, selection: [] }
            : { expanded: true });
    }

    render() {
        const { thumbnail,
            items,
            title = "",
            onSelected,
            onSelectViewAll,
            showThemeThumbnails,
            selectViewAllTitle,
            thumbnailProps,
            allowMultiSelect = false,
            showUserTheme } = this.props;
        const resolvedSelectViewAllTitle = selectViewAllTitle ?? t("View All Results");

        const { expanded, selection, selectionResetCount } = this.state;

        if (items.length === 0) {
            return null;
        }

        const gridProps = {
            forcedColumnCount: 4,
            columns: 3,
            colGap: 30,
            rowGap: 20,
            items,
            thumbnailClass: thumbnail,
            thumbnailProps,
            showThemeThumbnails: showThemeThumbnails || showUserTheme
        };

        return (<SearchPreview>
            <SearchPreviewHeader>
                <StyledHeader>
                    {this.allowCollapse &&
                        <div
                            className={`expander ${expanded ? "expanded" : "collapsed"}`}
                            onClick={this.handleToggleExpand}
                        >
                            <ArrowRight />
                        </div>
                    }
                    {title && <SearchPreviewTitle onClick={this.handleToggleExpand}>{title}</SearchPreviewTitle>}
                </StyledHeader>
                <StyledHeader>
                    {allowMultiSelect && selection.length > 0 &&
                        <AddSelectionButton onClick={this.handleAddSelection}>
                            {t("Add Selected Slides")}
                        </AddSelectionButton>
                    }
                    {onSelectViewAll && <p onClick={onSelectViewAll}>{resolvedSelectViewAllTitle}</p>}
                </StyledHeader>
            </SearchPreviewHeader>
            {expanded &&
                <>
                    <Gap10 />
                    {allowMultiSelect
                        ? <MultiSelectThumbnailGrid
                            key={selectionResetCount}
                            {...gridProps}
                            allowMultiSelect
                            onItemSelected={this.handleSelectionChange}
                            onDoubleClick={item => onSelected([item])}
                        />
                        : <ThumbnailGrid
                            {...gridProps}
                            onItemDoubleClick={onSelected}
                            onItemSelected={onSelected}
                        />
                    }
                    <Gap10 />
                </>
            }
        </SearchPreview>);
    }
};
